import { useOrg } from '../hooks/useOrg'

interface OrgBrandingHeaderProps {
  /** Fallback title when no org branding */
  defaultTitle?: string
  /** Subtitle text */
  subtitle?: string
  /** Show default u.software branding when no org */
  showDefaultBranding?: boolean
}

/**
 * Org branding header component
 * Displays the organization's logo and name, or u.software branding as fallback
 */
export function OrgBrandingHeader({
  defaultTitle,
  subtitle,
  showDefaultBranding = true,
}: OrgBrandingHeaderProps) {
  const { slug, name, branding, loading } = useOrg()

  // Loading state
  if (loading) {
    return (
      <div className="mb-8 text-center animate-pulse">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[var(--bg-elevated-2)]" />
        <div className="h-4 w-32 mx-auto bg-[var(--bg-elevated-2)] rounded" />
      </div>
    )
  }

  // Org branding
  if (slug && name) {
    return (
      <div className="mb-8 text-center">
        {branding.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt={name}
            className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-sm object-contain"
          />
        ) : (
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-sm"
            style={{
              background: branding.primaryColor
                ? `linear-gradient(135deg, ${branding.primaryColor}, ${branding.secondaryColor || branding.primaryColor})`
                : 'linear-gradient(135deg, var(--text-primary), var(--text-secondary))',
            }}
          >
            <span className="text-2xl font-bold text-[var(--bg-base)]">
              {name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        {subtitle && (
          <p className="text-sm text-[var(--text-secondary)]">{subtitle}</p>
        )}
        <p className="font-medium text-[var(--text-primary)]">{name}</p>
      </div>
    )
  }

  // Default u.software branding
  if (!showDefaultBranding) {
    return null
  }

  return (
    <div className="mb-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[var(--text-primary)] to-[var(--text-secondary)] flex items-center justify-center shadow-sm">
        <span className="text-2xl font-bold text-[var(--bg-base)]">u</span>
      </div>
      {defaultTitle && (
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
          {defaultTitle}
        </h1>
      )}
    </div>
  )
}

/**
 * Powered by u.software footer
 * Only shown when org branding.showPoweredBy is true
 */
export function PoweredByFooter() {
  const { slug, branding } = useOrg()

  // Only show for orgs with showPoweredBy enabled
  if (!slug || !branding.showPoweredBy) {
    return null
  }

  return (
    <div className="mt-8 text-center">
      <a
        href="https://u.software"
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
      >
        Powered by <span className="font-medium">u.software</span>
      </a>
    </div>
  )
}

/**
 * Org logo component for use in headers
 */
export function OrgLogo({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const { slug, name, branding, loading } = useOrg()

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  }

  const textSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  }

  if (loading) {
    return (
      <div className={`${sizeClasses[size]} rounded-lg bg-[var(--bg-elevated-2)] animate-pulse`} />
    )
  }

  // Org logo
  if (slug && name) {
    if (branding.logoUrl) {
      return (
        <img
          src={branding.logoUrl}
          alt={name}
          className={`${sizeClasses[size]} rounded-lg object-contain`}
        />
      )
    }

    return (
      <div
        className={`${sizeClasses[size]} rounded-lg flex items-center justify-center`}
        style={{
          background: branding.primaryColor
            ? `linear-gradient(135deg, ${branding.primaryColor}, ${branding.secondaryColor || branding.primaryColor})`
            : 'linear-gradient(135deg, var(--text-primary), var(--text-secondary))',
        }}
      >
        <span className={`${textSizes[size]} font-bold text-[var(--bg-base)]`}>
          {name.charAt(0).toUpperCase()}
        </span>
      </div>
    )
  }

  // Default u.software logo
  return (
    <div
      className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-[var(--text-primary)] to-[var(--text-secondary)] flex items-center justify-center`}
    >
      <span className={`${textSizes[size]} font-bold text-[var(--bg-base)]`}>u</span>
    </div>
  )
}

/**
 * Registration mode notice component
 * Shows appropriate message based on org registration settings
 */
export function RegistrationModeNotice() {
  const { slug, registration } = useOrg()

  // Only show for orgs with restricted registration
  if (!slug) {
    return null
  }

  if (registration.mode === 'invite_only') {
    return (
      <div className="mb-4 p-3 bg-[var(--warning-muted)]/30 border border-[var(--warning-muted)] rounded-lg">
        <p className="text-sm text-[var(--warning)]">
          Registration is invite-only. You need an invitation code to create an account.
        </p>
      </div>
    )
  }

  if (registration.mode === 'domain_restricted' && registration.allowedDomains.length > 0) {
    return (
      <div className="mb-4 p-3 bg-[var(--info-muted)]/30 border border-[var(--info-muted)] rounded-lg">
        <p className="text-sm text-[var(--info)]">
          Registration is restricted to email addresses from:{' '}
          <span className="font-medium">
            {registration.allowedDomains.join(', ')}
          </span>
        </p>
      </div>
    )
  }

  return null
}

/**
 * Check if an email domain is allowed for registration
 */
export function isEmailDomainAllowed(email: string, org: { registration: { mode: string; allowedDomains: string[] } }): boolean {
  if (org.registration.mode !== 'domain_restricted') {
    return true
  }

  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) {
    return false
  }

  return org.registration.allowedDomains.some(
    (allowed) => allowed.toLowerCase() === domain
  )
}
